import React from 'react';

import { useLoaderContext } from '~/providers/loader-context';
import { useServicesContext } from '~/providers/services-context';
import { CookieService } from '~/services/actions/cookie-actions';
import {
	getDefaultState,
	getStateName,
	type IStateObject,
	isValidState,
	type StateCode,
	type StateName,
} from '~/services/actions/state-actions';
import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from '~/services/actions/tracking-actions';

import useStore from '../../../../store';

import DesktopDropDown from './components/desktop-dropdown';
import MobileDropDown from './components/mobile-dropdown';

/**
 *
 *  refactored from class based component
 *
 * */

// Use state from cookie if present, otherwise use state from geoIP if supported, otherwise use default state.
function StateDropdown() {
	const {
		siteConfig: { statesAssignments, supportedStates },
	} = useServicesContext();

	const { state: stateFromIp } = useLoaderContext();

	const CookieServices = React.useMemo(() => new CookieService(), []);
	const trackingActions = React.useMemo(() => new TrackingActions(), []);

	const [usState, setUsState] = useStore(state => [
		state.usState,
		state.setUsState,
	]);

	const flattenedStateCodes: string[] = Object.values(
		supportedStates || {},
	).flat();

	const sortedStates: IStateObject[] = flattenedStateCodes
		.map(value => ({
			stateCode: value as StateCode,
			stateName: getStateName(value as StateCode) as StateName,
		}))
		.sort((a, b) => (a.stateName > b.stateName ? 1 : -1));

	const ref = (usState || stateFromIp) as StateCode;

	const selectedState = (
		isValidState(ref) && flattenedStateCodes.includes(ref)
			? ref
			: statesAssignments?.[ref] ?? getDefaultState(ref)
	) as StateCode;

	React.useEffect(() => {
		const cookie = CookieServices.select('us_state').getString();
		if (cookie && cookie !== selectedState) {
			setUsState(cookie as StateCode);
		}
	}, [CookieServices, setUsState, selectedState, stateFromIp]);

	const trackStateChange = (selectedState: StateCode): void => {
		trackingActions.eventTracking({
			event: TRACKING_EVENTS.FREE_BETS_STATE_SELECTOR,
			eventAction: TRACKING_ACTIONS.state_selector,
			eventCategory: TRACKING_CATEGORIES.NAVIGATION,
			eventLabel: getStateName(selectedState),
		});
	};

	const setSelectedState = (newState: StateCode) => {
		trackStateChange(newState);

		setUsState(newState);

		CookieServices.select('us_state').set(
			newState,
			new Date().getTime() + 86400000,
		);

		window.location.reload();
	};

	// do not render the state dropdown if states not configured
	if (!sortedStates.length) return null;

	// dropdown display set in css for hydration reasons
	return (
		<>
			<DesktopDropDown
				selectedState={selectedState}
				setSelectedState={setSelectedState}
				supportedStates={sortedStates}
			/>
			<MobileDropDown
				selectedState={selectedState}
				setSelectedState={setSelectedState}
				supportedStates={sortedStates}
			/>
		</>
	);
}

export default StateDropdown;
